import React, { useEffect, useState } from 'react';
import {
    View,
    TouchableOpacity,
    ScrollView,
    ActivityIndicator,
    Image,
    NativeSyntheticEvent,
    TextInputKeyPressEventData,
} from 'react-native';
import moment from 'moment';
import personDefaultPicture from '../../../../assets/person-default-picture.png';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import { Pagination } from '../../../components/Pagination';
import { facialService } from '../../../services/central-api/facial';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { FacialParamList } from '../../../typings/Params';
import CustomButton from '../../../components/CustomButton';
import PeopleFilter from './Filters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import useFilters from '../../../components/Filter/hooks/useFilters';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableContainer from '../../../components/DataTableContainer';
import DataTableHeader from '../../../components/DataTableHeader';
import { useQuery } from '@tanstack/react-query';

interface PeopleListParams {
    navigation: Navigation;
}

export default function PeopleList({ navigation }: PeopleListParams) {
    const { theme } = useStyles(stylesheet);

    const route = useRoute<RouteProp<FacialParamList, 'PeopleList'>>();

    const [filters, setFilters] = useFilters<FacialParamList['PeopleList'] & { photo?: File; }>({
        ...route.params,
        page: Math.max(route.params?.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });
    const [filterName, setFilterName] = useState(filters.nameOrDocument ?? '');
    const [file, setFile] = useState<File | undefined>(filters.photo);
    const [previewUrl, setPreviewUrl] = useState<string | undefined>();

    const isFocused = useIsFocused();

    const { data, isFetching } = useQuery({
        queryKey: ['getFacialPeople', filters],
        queryFn: () => !filters.photo ? facialService.getFacialPeople(filters).then(result => ({ rows: result.rows, count: result.count })) : facialService.findPeopleByPicture({
            photo: filters.photo,
            onlyActive: false
        }).then(result => ({ rows: result.people, count: 0 })),
        enabled: isFocused,
    });

    useEffect(() => {
        if (!file) {
            setPreviewUrl(undefined);
            return;
        }
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    function handleFilters() {
        setFilters({
            ...filters,
            nameOrDocument: filterName,
            photo: undefined,
            page: 0
        });

        navigation.setParams({
            nameOrDocument: filterName,
            page: 1
        });
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            handleFilters();
        }
    }

    return <Container>
        <Header
            title={translate('registeredPeople')}
            actions={<CustomButton onPress={() => navigation.navigate('PersonForm')} text={translate('register')} />}
        />
        <Card>
            <DataTableHeader
                title={translate('registeredPeople')}
                zIndex={2}
                filter={<PeopleFilter
                    filters={filters}
                    setFilters={setFilters}
                    textFilter={filterName}
                    setTextFilter={setFilterName}
                    file={file}
                    setFile={setFile}
                    previewUrl={previewUrl}
                />}
                filterInput={<FilterInput
                    value={filterName}
                    onChange={setFilterName}
                    placeholder={translate('typeToSearch')}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<ScrollView contentContainerStyle={{ padding: 16, flexGrow: 1 }}>
                    <View style={{ gap: 10, flex: 1 }}>
                        {isFetching ?
                            <ActivityIndicator size='small' color={theme.colors.loadingColor} />
                            : !data?.rows.length ?
                                <View style={{ flex: 1, alignItems: 'center', justifyContent: 'flex-start' }}>
                                    <MyAppText style={{ color: theme.colors.labelColor }}>
                                        {translate('noResults')}.
                                    </MyAppText>
                                </View>
                                : <View style={[{
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    columnGap: 14
                                }]}>
                                    {data?.rows.map((person) =>
                                        <TouchableOpacity
                                            key={JSON.stringify(person)}
                                            onPress={async () => {
                                                navigation.navigate('PersonForm', { id: person.id });
                                            }}
                                        >
                                            <View style={[{
                                                width: 150,
                                                height: 250,
                                                borderRadius: 5,
                                                rowGap: 10
                                            }]}>
                                                <Image
                                                    style={{
                                                        height: 168, width: 144,
                                                        borderRadius: 8,
                                                        overflow: 'hidden',
                                                    }}
                                                    source={person.facePictures.length ? { uri: person.facePictures[0].imageUrl } : personDefaultPicture}
                                                />
                                                <View>
                                                    <MyAppText style={{ color: theme.colors.textColor, fontWeight: '500', fontSize: 14 }}>
                                                        {person.name}
                                                    </MyAppText>
                                                    <MyAppText style={{ color: theme.colors.textColor, fontWeight: '500', fontSize: 9 }}>
                                                        {translate('addedOn')} {moment(person.createdAt ? new Date(person.createdAt) : undefined).format('DD/MM/YYYY HH:mm')}
                                                    </MyAppText>
                                                    <MyAppText style={{ color: theme.colors.textColor, fontWeight: '500', fontSize: 11 }}>
                                                        {person.isActive ? translate('active') : translate('inactive')}
                                                    </MyAppText>
                                                </View>
                                            </View>
                                        </TouchableOpacity>
                                    )}
                                </View>
                        }
                    </View>
                </ScrollView>}
                pagination={!filters.photo && <Pagination
                    totalItems={data?.count ?? 0}
                    currentPage={filters.page}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />
        </Card>
    </Container>;
}

const stylesheet = createStyleSheet(() => ({}));
